import React from 'react';
import { Box, Typography, Chip } from '@mui/material';
import { FaArrowLeft } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { Container, Header, HeaderLeft, BackButton, Title, Content, MainSection } from '../styles';
import ConfirmationModal from '../ConfirmationModal';
import ImportToolbar from './ImportToolbar';
import ImportTable from './ImportTable';
import { useImportManager } from './hooks/useImportManager';
import { useAlert } from '../../../contexts/AlertContext';


const StatusLegend = ({ title, statuses }) => (
  <Box sx={{ mt: 3, p: 2, backgroundColor: '#F9FAFB', borderRadius: '8px' }}>
    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
      {title}
    </Typography>
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {Object.entries(statuses).map(([key, statusConfig]) => (
        <Box key={key} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Chip
            label={statusConfig.label}
            size="small"
            variant="outlined"
            sx={{
              borderColor: statusConfig.color,
              color: statusConfig.color,
              minWidth: '140px'
            }}
          />
          <Typography variant="body2" color="textSecondary">
            {statusConfig.tooltip}
          </Typography>
        </Box>
      ))}
    </Box>
  </Box>
);

const FormatGuide = ({ config }) => (
  <>
    <Box sx={{ mt: 3, p: 2, backgroundColor: '#F9FAFB', borderRadius: '8px' }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
        CSV File Format
      </Typography>
      <Typography variant="body2" color="textSecondary">
        The CSV file must include the following columns (in any order):
      </Typography>
      <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
        <strong>Required:</strong> {config.requiredFields.join(', ')}
      </Typography>
      {config.optionalFields?.length > 0 && (
        <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
          <strong>Optional:</strong> {config.optionalFields.join(', ')}
        </Typography>
      )}
      {config.documentation?.fileFormat?.dateFormat && (
        <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
          Date format: {config.documentation.fileFormat.dateFormat}
        </Typography>
      )}
      {config.documentation?.fileFormat?.notes?.map((note, index) => (
        <Typography key={index} variant="body2" color="textSecondary" sx={{ mt: 1 }}>
          <strong>Note:</strong> {note}
        </Typography>
      ))}
    </Box>

    {config.statusConfig.userStatuses && (
      <StatusLegend title="User Status Legend" statuses={config.statusConfig.userStatuses} />
    )}

    {config.statusConfig.projectStatuses && (
      <StatusLegend title="Project Status Legend" statuses={config.statusConfig.projectStatuses} />
    )}

    {config.statusConfig.assignmentStatuses && (
      <StatusLegend title="Assignment Status Legend" statuses={config.statusConfig.assignmentStatuses} />
    )}
  </>
);

const ImportContainer = ({ 
  config, 
  title, 
  backPath = '/data-import',
  backLabel = 'Back to Data Import' 
}) => {
  const navigate = useNavigate();
  const { showAlert } = useAlert();
  
  const {
    state,
    actions,
    fieldContext
  } = useImportManager(config);

  const handleImportClick = () => {
    const itemsToImport = config.helpers.getImportableItems(state.data);
    
    if (itemsToImport.length === 0) {
      return; // Tooltip will show appropriate message
    }

    actions.setImportModal({ open: true, count: itemsToImport.length });
  };

  const handleDeleteClick = (itemId) => {
    actions.setDeleteModal({ open: true, itemId });
  };

  const handleDeleteConfirm = () => {
    actions.deleteItem(state.deleteModal.itemId);
  };

  return (
    <Container>
      <Header>
        <HeaderLeft>
          <BackButton onClick={() => navigate(backPath)}>
            <FaArrowLeft /> {backLabel}
          </BackButton>
          <Title>{title}</Title>
        </HeaderLeft>
      </Header>

      <Content>
        <MainSection>
          <Typography variant="body1" sx={{ mb: 2 }}>
            {config.documentation?.description || `Upload a CSV file with ${config.entityName.toLowerCase()} data to import into the system`}
          </Typography>

          <Box sx={{ 
            backgroundColor: '#F3F4F6',
            borderRadius: '8px',
            padding: '16px'
          }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', backgroundColor: 'white', borderRadius: '4px', overflow: 'hidden' }}>
              <ImportToolbar
                config={config}
                data={state.data}
                loading={state.loading}
                validating={state.validating}
                onRevalidate={actions.revalidate}
                onImport={handleImportClick}
                onFileProcess={actions.processFile}
                showAlert={showAlert}
              />
              {state.data.length > 0 && (
                <ImportTable
                  config={config}
                  data={state.data}
                  loading={state.loading}
                  editingCell={state.editingCell}
                  isSelectOpen={state.isSelectOpen}
                  onCellEdit={actions.updateField}
                  onDeleteItem={handleDeleteClick}
                  onToggleErrorPanel={actions.toggleDetailPanel}
                  expandedErrorPanels={state.detailPanelExpandedRowIds}
                  fieldContext={fieldContext}
                  onSelectOpen={actions.setSelectOpen.bind(null, true)}
                  onSelectClose={() => {
                    actions.setSelectOpen(false);
                    // Delay exit to allow the value to be set, but only if no modal is open
                    setTimeout(() => {
                      // Check if any modal is currently open before exiting edit mode
                      const hasOpenModal = document.querySelector('.MuiModal-root:not([aria-hidden="true"])') ||
                                          document.querySelector('.MuiDialog-root:not([aria-hidden="true"])') ||
                                          document.querySelector('[role="dialog"]:not([aria-hidden="true"])');
                      
                      if (!hasOpenModal) {
                        actions.setEditingCell(null);
                      }
                    }, 250);
                  }}
                  onStartEdit={(id, field) => {
                    actions.setEditingCell({ id, field });
                  }}
                  onStopEdit={() => actions.setEditingCell(null)}
                />
              )}
            </Box>
          </Box>

          <FormatGuide config={config} />
        </MainSection>
      </Content>

      <ConfirmationModal
        isOpen={state.deleteModal.open}
        onClose={() => actions.setDeleteModal({ open: false, itemId: null })}
        onConfirm={handleDeleteConfirm}
        title={`Remove ${config.entityName}`}
        message={`Are you sure you want to remove this ${config.entityName.toLowerCase()} from the import list?`}
      />

      <ConfirmationModal
        isOpen={state.importModal.open}
        onClose={() => actions.setImportModal({ open: false, count: 0 })}
        onConfirm={actions.importData}
        title={`Import ${config.entityNamePlural}`}
        message={`Are you sure you want to import ${state.importModal.count} ${state.importModal.count === 1 ? config.entityName.toLowerCase() : config.entityNamePlural.toLowerCase()}? This action cannot be undone.`}
        actionButtonText="Import"
      />
    </Container>
  );
};

export default ImportContainer; 